import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { ChefHat, CheckCircle, XCircle, AlertTriangle, Calendar, Users } from "lucide-react";
import PageHeader from "../../components/PageHeader";

const ESTATUS_LABEL = {
  pendiente_aprobacion: { label: "Pendiente de aprobación", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  aprobada: { label: "Aprobada", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  rechazada: { label: "Rechazada", cls: "bg-red-100 text-red-800 border-red-300" },
  producida: { label: "Producida", cls: "bg-blue-100 text-blue-800 border-blue-300" },
};

const Produccion = () => {
  const [producciones, setProducciones] = useState([]);
  const [eventos, setEventos] = useState({});
  const [clientes, setClientes] = useState({});
  const [loading, setLoading] = useState(true);
  const [rechazarDialog, setRechazarDialog] = useState(null);
  const [motivoRechazo, setMotivoRechazo] = useState("");
  const [filtro, setFiltro] = useState("pendiente_aprobacion");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, eRes, cRes] = await Promise.all([
        axios.get(`${API}/produccion`),
        axios.get(`${API}/eventos`),
        axios.get(`${API}/clientes`),
      ]);
      setProducciones(pRes.data || []);
      const eMap = {};
      (eRes.data || []).forEach(e => { eMap[e.evento_id] = e; });
      setEventos(eMap);
      const cMap = {};
      (cRes.data || []).forEach(c => { cMap[c.cliente_id] = c; });
      setClientes(cMap);
    } catch {
      toast.error("Error al cargar producciones");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAprobar = async (id) => {
    if (!window.confirm("¿Aprobar producción? Esto descontará los insumos del inventario.")) return;
    try {
      const res = await axios.post(`${API}/produccion/${id}/aprobar`);
      toast.success(`Aprobada. ${res.data.movimientos_inventario || 0} movimientos de inventario.`);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al aprobar");
    }
  };

  const handleRechazar = async () => {
    if (!rechazarDialog || !motivoRechazo.trim()) {
      toast.error("Indica el motivo del rechazo");
      return;
    }
    try {
      await axios.post(`${API}/produccion/${rechazarDialog.produccion_id}/rechazar`, { motivo: motivoRechazo.trim() });
      toast.success("Producción rechazada. Alerta creada. (No bloquea ventas)");
      setRechazarDialog(null);
      setMotivoRechazo("");
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al rechazar");
    }
  };

  const filtradas = filtro === "todas" ? producciones : producciones.filter(p => p.estatus === filtro);
  const pendientes = producciones.filter(p => p.estatus === "pendiente_aprobacion").length;

  return (
    <div className="space-y-6">
      <PageHeader icon={ChefHat} title="Producción" subtitle="Aprueba lo que va a cocina" />

      {/* Botones grandes de filtro */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { key: "pendiente_aprobacion", label: "Pendientes", color: "amber", count: pendientes },
          { key: "aprobada", label: "Aprobadas", color: "emerald", count: producciones.filter(p => p.estatus === "aprobada").length },
          { key: "rechazada", label: "Rechazadas", color: "red", count: producciones.filter(p => p.estatus === "rechazada").length },
          { key: "todas", label: "Todas", color: "stone", count: producciones.length },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFiltro(f.key)}
            className={`p-4 rounded-md text-left transition border-2 ${filtro === f.key ? `border-${f.color}-500 bg-${f.color}-50` : "border-stone-200 bg-white hover:border-stone-300"}`}
            data-testid={`filter-${f.key}`}
          >
            <p className="text-xs uppercase tracking-wide text-stone-500">{f.label}</p>
            <p className="font-display text-3xl text-stone-900 mt-1">{f.count}</p>
          </button>
        ))}
      </div>

      {pendientes > 0 && filtro !== "pendiente_aprobacion" && (
        <div className="bg-amber-50 border border-amber-200 rounded p-3 flex items-center gap-2 text-sm text-amber-900">
          <AlertTriangle className="w-4 h-4" />
          <span>Tienes <strong>{pendientes}</strong> producciones esperando tu aprobación.</span>
          <Button size="sm" variant="link" onClick={() => setFiltro("pendiente_aprobacion")}>Ver ahora</Button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-32"><div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin" /></div>
      ) : filtradas.length === 0 ? (
        <Card className="border-stone-200"><CardContent className="p-12 text-center">
          <ChefHat className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <h3 className="font-display text-xl text-stone-900 mb-1">Sin producciones en este filtro</h3>
          <p className="text-stone-500 text-sm">Cuando se firme un contrato se generará una producción automáticamente.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {filtradas.map(p => {
            const ev = eventos[p.evento_id];
            const cl = clientes[p.cliente_id];
            const estatus = ESTATUS_LABEL[p.estatus] || { label: p.estatus, cls: "bg-stone-100 text-stone-700" };
            return (
              <Card key={p.produccion_id} className="border-stone-200" data-testid={`produccion-${p.produccion_id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-display text-lg text-stone-900">{ev?.nombre || "Evento"}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${estatus.cls}`}>{estatus.label}</span>
                      </div>
                      <p className="text-xs text-stone-500">{cl?.nombre || "—"} · {ev?.fecha || p.fecha_evento}</p>
                    </div>
                    <div className="flex gap-2">
                      {p.estatus === "pendiente_aprobacion" && (
                        <>
                          <Button size="sm" onClick={() => handleAprobar(p.produccion_id)} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid={`btn-aprobar-${p.produccion_id}`}>
                            <CheckCircle className="w-3.5 h-3.5 mr-1" /> Aprobar
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setRechazarDialog(p); setMotivoRechazo(""); }} className="border-red-300 text-red-600" data-testid={`btn-rechazar-${p.produccion_id}`}>
                            <XCircle className="w-3.5 h-3.5 mr-1" /> Rechazar
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    <div className="bg-stone-50 p-2 rounded">
                      <p className="text-stone-500 text-[10px] uppercase">Invitados</p>
                      <p className="font-medium text-stone-900 flex items-center gap-1"><Users className="w-3 h-3" />{(p.adultos || 0) + (p.ninos || 0)} ({p.adultos} adultos, {p.ninos} niños)</p>
                    </div>
                    <div className="bg-stone-50 p-2 rounded">
                      <p className="text-stone-500 text-[10px] uppercase">Fecha evento</p>
                      <p className="font-medium text-stone-900 flex items-center gap-1"><Calendar className="w-3 h-3" />{p.fecha_evento}</p>
                    </div>
                    <div className="bg-stone-50 p-2 rounded">
                      <p className="text-stone-500 text-[10px] uppercase">Recetas</p>
                      <p className="font-medium text-stone-900">{(p.recetas || []).length}</p>
                    </div>
                    <div className="bg-stone-50 p-2 rounded">
                      <p className="text-stone-500 text-[10px] uppercase">Insumos extra</p>
                      <p className="font-medium text-stone-900">{(p.insumos_extra || []).length}</p>
                    </div>
                  </div>
                  {p.notas && <p className="text-xs text-stone-500 mt-2 italic">{p.notas}</p>}
                  {p.motivo_rechazo && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                      <strong>Rechazada:</strong> {p.motivo_rechazo} ({p.rechazada_por})
                    </div>
                  )}
                  {p.aprobada_at && (
                    <div className="mt-2 p-2 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-700">
                      <strong>Aprobada</strong> el {new Date(p.aprobada_at).toLocaleString("es-MX")} por {p.aprobada_por}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Rechazar Dialog */}
      <Dialog open={!!rechazarDialog} onOpenChange={() => { setRechazarDialog(null); setMotivoRechazo(""); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-red-700">Rechazar producción</DialogTitle>
            <DialogDescription>
              Esta acción genera una alerta pero <strong>no bloquea</strong> ventas ni contratos. Indica el motivo.
            </DialogDescription>
          </DialogHeader>
          <Textarea value={motivoRechazo} onChange={(e) => setMotivoRechazo(e.target.value)} rows={3} placeholder="Ej: Stock insuficiente, fecha imposible, cliente cambia menú..." className="border-stone-300" data-testid="input-motivo-rechazo" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRechazarDialog(null)} className="border-stone-300">Cancelar</Button>
            <Button onClick={handleRechazar} className="bg-red-600 hover:bg-red-700 text-white" data-testid="btn-confirmar-rechazar">Rechazar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Produccion;
